import ActBadges from '../../components/ui/ActBadges'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import ConfirmModal from '../../components/ConfirmModal'
import EntityComments from '../../components/EntityComments'
import { ActionMenu, Button, Select } from '../../components/ui'
import Icon from '../../components/Icon'
import { formatReportRub } from '../../utils/formatRub'
import { EditableReportTitle } from './reports/IncomeReportComponents'
import './ContractReportPreview.css'
import './reports/Reports.css'

function currentPeriod() {
  const now = new Date()
  const year = now.getFullYear()
  const quarter = Math.ceil((now.getMonth() + 1) / 3)
  return { year, quarter }
}

function sectionTitleForRow(row) {
  if (row?.row_type !== 'team') return null
  const s = (row.section || '').trim()
  if (s === 'Производственное') return 'Производственные'
  if (s === 'Обслуживание') return 'Обслуживание'
  return s || null
}

function sectionToneForTitle(title) {
  if (title === 'Производственные') return 'production'
  if (title === 'Обслуживание') return 'service'
  if (title === 'Прочие расходы') return 'other'
  return 'default'
}

function formatActDate(v) {
  if (v == null || v === '') return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('ru-RU')
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const cleaned = String(value).trim().replace(/\s/g, '').replace(/[^\d.,-]/g, '')
  if (!cleaned) return null
  const num = parseFloat(cleaned.replace(',', '.'))
  return Number.isFinite(num) ? num : null
}

function SummaryActsReportPreview({ reportId = null }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  // frozen — режим сохранённого снимка отчёта (открыт из списка отчётов):
  // период не меняется, данные берутся из сохранённого payload.
  const frozen = Boolean(reportId)
  // Прячем верхнюю «полосу» Layout с заголовком «Страница» — у отчёта своя шапка.
  usePageHeader({ title: null, hideTopHeader: true }, [])
  const defaultPeriod = currentPeriod()
  // Начальный период можно задать через query-параметры (переход из «Создать отчёт»).
  const initialPeriod = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    const rawPeriod = sp.get('period')
    const period = rawPeriod === 'year' || rawPeriod === 'quarter' ? rawPeriod : 'quarter'
    const year = Number(sp.get('year'))
    const quarter = Number(sp.get('quarter'))
    return {
      period,
      year: Number.isFinite(year) && year > 0 ? year : defaultPeriod.year,
      quarter: quarter >= 1 && quarter <= 4 ? quarter : defaultPeriod.quarter,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState('')
  const [reportMeta, setReportMeta] = useState(null)
  const [canEditReport, setCanEditReport] = useState(false)
  const [periodType, setPeriodType] = useState(initialPeriod.period)
  const [selectedYear, setSelectedYear] = useState(initialPeriod.year)
  const [selectedQuarter, setSelectedQuarter] = useState(initialPeriod.quarter)
  // Подпись периода для сохранённого снимка (приходит из меты отчёта).
  const [snapshotLabel, setSnapshotLabel] = useState('')
  const canArchive = frozen && user?.permissions?.['reports.archive'] === true && Boolean(reportMeta) && !reportMeta?.archived_at

  const fetchReport = async () => {
    setLoading(true)
    setError('')
    try {
      if (frozen) {
        const response = await api.get(`/company/reports/${reportId}/summary`)
        setPayload(response.data?.payload || null)
        const nextReport = response.data?.report || null
        setReportMeta(nextReport)
        setCanEditReport(Boolean(response.data?.can_edit))
        setSnapshotLabel(nextReport?.period_label || '')
      } else {
        const params = { period: periodType, year: selectedYear }
        if (periodType === 'quarter') params.quarter = selectedQuarter
        const response = await api.get('/company/reports/summary-acts', { params })
        setPayload(response.data || null)
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить сводный отчёт')
      setPayload(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozen, reportId, periodType, selectedYear, selectedQuarter])

  const periodLabel = useMemo(() => {
    if (frozen) return snapshotLabel || (payload?.period_label ?? '')
    if (periodType === 'quarter') return `${selectedQuarter} кв. ${selectedYear} г.`
    return `Год ${selectedYear}`
  }, [frozen, snapshotLabel, payload, periodType, selectedYear, selectedQuarter])

  const downloadFile = async (format) => {
    setDownloadLoading(true)
    setError('')
    try {
      let url
      if (frozen) {
        url = `/company/reports/${reportId}/summary/${format}`
      } else {
        const params = new URLSearchParams({ period: periodType, year: String(selectedYear) })
        if (periodType === 'quarter') params.set('quarter', String(selectedQuarter))
        url = `/company/reports/summary-acts/${format}?${params.toString()}`
      }
      const response = await api.get(url, {
        responseType: 'blob',
      })
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8',
      })
      const cd = response.headers['content-disposition']
      let filename = `svodnyy-otchjot-akty-${new Date().toISOString().slice(0, 10)}.${format}`
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || cd.match(/filename=["']?([^"';]+)["']?/i)
        if (m?.[1]) filename = m[1].trim()
      }
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      let msg = e.response?.data?.error
      const raw = e.response?.data
      if (!msg && raw instanceof Blob) {
        try {
          const text = await raw.text()
          const parsed = JSON.parse(text)
          msg = parsed?.error
        } catch {
          msg = null
        }
      }
      setError(msg || `Не удалось скачать ${format.toUpperCase()}`)
    } finally {
      setDownloadLoading(false)
    }
  }

  const archiveReport = async () => {
    if (!reportId) return
    setArchiving(true)
    setArchiveError('')
    try {
      await api.patch(`/company/reports/${reportId}/archive`)
      setArchiveOpen(false)
      navigate('/company/reports')
    } catch (e) {
      setArchiveError(e.response?.data?.error || 'Не удалось архивировать отчет')
    } finally {
      setArchiving(false)
    }
  }

  const saveReportTitle = async (title) => {
    const response = await api.patch(`/company/reports/${reportId}`, { title })
    setReportMeta((previous) => response.data?.report || { ...previous, title })
  }

  const columns = payload?.columns || []
  const matrixRows = payload?.matrix_rows || []
  const columnTotals = payload?.column_totals || {}
  const acts = payload?.acts || []

  // Колонки матрицы группируем по объекту (алфавит); объекты без названия — в конец.
  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => {
      const ao = (a.object_name || '￿').toLowerCase()
      const bo = (b.object_name || '￿').toLowerCase()
      const byObject = ao.localeCompare(bo, 'ru')
      if (byObject !== 0) return byObject
      return String(a.column_label || a.stage_id).localeCompare(String(b.column_label || b.stage_id), 'ru')
    })
  }, [columns])

  const objectGroups = useMemo(() => {
    const groups = []
    let current = null
    for (const col of sortedColumns) {
      const name = col.object_name || ''
      const key = name || '—'
      if (!current || current.key !== key) {
        current = { key, object_name: name, object_id: col.object_id || null, columns: [] }
        groups.push(current)
      }
      current.columns.push(col)
    }
    return groups
  }, [sortedColumns])

  const stageToneById = useMemo(() => {
    const tones = {}
    objectGroups.forEach((group, groupIndex) => {
      const tone = groupIndex % 2 === 0 ? 'light' : 'muted'
      group.columns.forEach((col) => {
        tones[col.stage_id] = tone
      })
    })
    return tones
  }, [objectGroups])

  // Акты группируем по объекту, внутри — по договору (оба по алфавиту, затем по дате).
  // Флаги isFirstOfObject / isFirstOfContract: показываем объект/договор один раз на группу.
  const groupedActs = useMemo(() => {
    const sorted = [...acts].sort((a, b) => {
      const ao = (a.object_name || '￿').toLowerCase()
      const bo = (b.object_name || '￿').toLowerCase()
      const byObject = ao.localeCompare(bo, 'ru')
      if (byObject !== 0) return byObject
      const ac = (a.contract_number || '￿').toLowerCase()
      const bc = (b.contract_number || '￿').toLowerCase()
      const byContract = ac.localeCompare(bc, 'ru')
      if (byContract !== 0) return byContract
      return String(a.issue_date || '').localeCompare(String(b.issue_date || ''))
    })
    const objectKeyOf = (a) => a.object_id || a.object_name || '—'
    const contractKeyOf = (a) => `${objectKeyOf(a)}::${a.contract_id || a.contract_number || '—'}`
    // Размеры групп для объединения ячеек (rowSpan).
    const objectCounts = {}
    const contractCounts = {}
    for (const a of sorted) {
      objectCounts[objectKeyOf(a)] = (objectCounts[objectKeyOf(a)] || 0) + 1
      contractCounts[contractKeyOf(a)] = (contractCounts[contractKeyOf(a)] || 0) + 1
    }
    let lastObjectKey = null
    let lastContractKey = null
    return sorted.map((a) => {
      const objectKey = objectKeyOf(a)
      const contractKey = contractKeyOf(a)
      const isFirstOfObject = objectKey !== lastObjectKey
      const isFirstOfContract = contractKey !== lastContractKey
      lastObjectKey = objectKey
      lastContractKey = contractKey
      return {
        ...a,
        isFirstOfObject,
        isFirstOfContract,
        objectRowSpan: isFirstOfObject ? objectCounts[objectKey] : 0,
        contractRowSpan: isFirstOfContract ? contractCounts[contractKey] : 0,
      }
    })
  }, [acts])

  const matrixRowsWithSections = useMemo(() => {
    const out = []
    let lastTeamSection = null
    let expenseHeaderDone = false
    for (const row of matrixRows) {
      if (row.row_type === 'other_expense' && !expenseHeaderDone) {
        out.push({ __section: true, label: 'Прочие расходы', sectionTone: 'other' })
        expenseHeaderDone = true
        lastTeamSection = null
      }
      if (row.row_type === 'team') {
        const st = sectionTitleForRow(row)
        if (st && st !== lastTeamSection) {
          lastTeamSection = st
          out.push({ __section: true, label: st, sectionTone: sectionToneForTitle(st) })
        }
      } else if (row.row_type !== 'other_expense') {
        lastTeamSection = null
      }
      out.push(row)
    }
    return out
  }, [matrixRows])

  const dateNow = new Date()
  const dateStr = dateNow.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = dateNow.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

  const openInNewTab = (to) => {
    window.open(to, '_blank', 'noopener,noreferrer')
  }

  // Кликабельная ячейка-ссылка.
  const linkCell = (label, to, { newTab = false } = {}) => (
    <span
      role="button"
      tabIndex={0}
      className="clickable-link"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (newTab) openInNewTab(to)
        else navigate(to)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          if (newTab) openInNewTab(to)
          else navigate(to)
        }
      }}
    >
      {label}
    </span>
  )

  return (
    <div className="contract-report-preview">
      <div className="contract-report-preview__toolbar">
        <EditableReportTitle
          report={reportMeta}
          readOnly={!frozen || !canEditReport}
          onSave={saveReportTitle}
          className="contract-report-preview__toolbar-title"
          fallback="Сводный отчёт"
        />
        <div className="contract-report-preview__toolbar-actions">
          {/* Снимок отчёта: период зафиксирован при создании — переключатели не показываем. */}
          {!frozen && (
            <>
              <label className="contract-report-preview__period-option">
                <input
                  type="radio"
                  name="summary-acts-period"
                  checked={periodType === 'quarter'}
                  onChange={() => setPeriodType('quarter')}
                />
                <span>Квартал</span>
              </label>
              <label className="contract-report-preview__period-option">
                <input
                  type="radio"
                  name="summary-acts-period"
                  checked={periodType === 'year'}
                  onChange={() => setPeriodType('year')}
                />
                <span>Год</span>
              </label>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="contract-report-preview__select--minw"
              >
                {Array.from({ length: 6 }, (_, i) => defaultPeriod.year - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
              {periodType === 'quarter' && (
                <Select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                  className="contract-report-preview__select--minw"
                >
                  <option value={1}>1 кв.</option>
                  <option value={2}>2 кв.</option>
                  <option value={3}>3 кв.</option>
                  <option value={4}>4 кв.</option>
                </Select>
              )}
            </>
          )}
          <Button variant="ghost" onClick={() => navigate(frozen ? '/company/reports' : '/company/stages')} className="contract-report-preview__btn-back">
            ← Назад
          </Button>
          <ActionMenu
            label="Действия"
            variant="primary"
            items={[
              {
                id: 'csv',
                label: 'Скачать CSV',
                icon: <Icon name="download" size={16} />,
                disabled: downloadLoading,
                onSelect: () => downloadFile('csv'),
              },
              {
                id: 'pdf',
                label: 'Скачать PDF',
                icon: <Icon name="download" size={16} />,
                disabled: downloadLoading,
                onSelect: () => downloadFile('pdf'),
              },
              canArchive && { type: 'divider', id: 'archive-divider' },
              canArchive && {
                id: 'archive',
                label: 'Архивировать',
                icon: <Icon name="trash" size={16} />,
                tone: 'danger',
                onSelect: () => setArchiveOpen(true),
              },
            ]}
          />
        </div>
      </div>

      {error && <div className="contract-report-preview__message contract-report-preview__message--error">{error}</div>}
      {archiveOpen && (
        <ConfirmModal
          title="Архивировать отчет?"
          message={<p>Отчет пропадет из списка активных отчетов. Данные снимка сохранятся в архиве.</p>}
          confirmLabel={archiving ? 'Архивация…' : 'Архивировать'}
          confirmVariant="danger"
          confirmDisabled={archiving}
          error={archiveError}
          onCancel={() => {
            if (!archiving) setArchiveOpen(false)
          }}
          onConfirm={archiveReport}
        />
      )}

      <div className="contract-report-preview__page pdf-page">
        <header className="contract-report-preview__header">
          <div className="contract-report-preview__header-left">
            <div className="contract-report-preview__logo">
              <img
                src="/logo/logo.png"
                alt="2ПБ"
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = '/logo/logo.jpg'
                }}
              />
            </div>
            <div>
              <h2 className="contract-report-preview__title">ПРОЕКТНАЯ АРТЕЛЬ</h2>
              <p className="contract-report-preview__subtitle">Сводный отчёт · По подписанным актам</p>
            </div>
          </div>
          <div className="contract-report-preview__header-right">
            <p className="contract-report-preview__date-label">Дата формирования</p>
            <p className="contract-report-preview__date">
              {dateStr} <span className="contract-report-preview__date-sep">|</span> {timeStr}
            </p>
          </div>
        </header>

        <section className="contract-report-preview__section">
          <div className="contract-report-preview__period-label">{periodLabel}</div>
          <div className="contract-report-preview__table-wrap contract-report-preview__table-wrap--scroll contract-report-preview__table-wrap--matrix">
            <table className="contract-report-preview__table contract-report-preview__table--matrix-sticky">
              <thead>
                <tr>
                  <th rowSpan={2} className="contract-report-preview__matrix-corner-head contract-report-preview__matrix-label-upper">
                    Сводный бюджет
                  </th>
                  <th rowSpan={2} className="contract-report-preview__num contract-report-preview__num--total-col contract-report-preview__matrix-label-upper">
                    Итого
                  </th>
                  {objectGroups.map((group) => (
                    <th
                      key={group.key}
                      colSpan={group.columns.length}
                      className={[
                        'contract-report-preview__matrix-object-head',
                        `contract-report-preview__matrix-object-head--tone-${stageToneById[group.columns[0]?.stage_id] || 'light'}`,
                      ].join(' ')}
                      title={group.object_name || 'Без объекта'}
                    >
                      {group.object_id ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            openInNewTab(`/object/${encodeURIComponent(group.object_id)}`)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              e.stopPropagation()
                              openInNewTab(`/object/${encodeURIComponent(group.object_id)}`)
                            }
                          }}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {group.object_name || 'Без объекта'}
                        </span>
                      ) : (
                        <span>{group.object_name || 'Без объекта'}</span>
                      )}
                    </th>
                  ))}
                </tr>
                <tr>
                  {sortedColumns.map((c) => {
                    const title = [c.column_label || c.stage_id, c.contract_number, c.object_name].filter(Boolean).join(' · ')
                    return (
                      <th
                        key={c.stage_id}
                        className={[
                          'contract-report-preview__num',
                          'contract-report-preview__stage-col-head',
                          `contract-report-preview__matrix-stage-cell--tone-${stageToneById[c.stage_id] || 'light'}`,
                        ].join(' ')}
                        title={title}
                      >
                        <span
                          className="contract-report-preview__stage-col-head-main"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            openInNewTab(`/stage/${encodeURIComponent(c.stage_id)}`)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              e.stopPropagation()
                              openInNewTab(`/stage/${encodeURIComponent(c.stage_id)}`)
                            }
                          }}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {c.column_label || c.stage_id}
                        </span>
                        {c.contract_number && (
                          <span className="contract-report-preview__stage-col-head-meta">{c.contract_number}</span>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={2 + columns.length}>Загрузка…</td>
                  </tr>
                ) : matrixRowsWithSections.length === 0 ? (
                  <tr>
                    <td colSpan={2 + columns.length}>Нет данных за выбранный период</td>
                  </tr>
                ) : (
                  matrixRowsWithSections.map((row, i) => {
                    if (row.__section) {
                      return (
                        <tr
                          key={`sec-${row.label}-${i}`}
                          className={[
                            'contract-report-preview__matrix-section-row',
                            `contract-report-preview__matrix-section-row--${row.sectionTone || 'default'}`,
                          ].join(' ')}
                        >
                          <td className="contract-report-preview__matrix-section-cell contract-report-preview__matrix-section-cell--col1">
                            {row.label}
                          </td>
                          <td
                            className="contract-report-preview__matrix-section-cell contract-report-preview__matrix-section-cell--col2"
                            aria-hidden="true"
                          />
                          {columns.length > 0 && (
                            <td
                              colSpan={columns.length}
                              className="contract-report-preview__matrix-section-cell contract-report-preview__matrix-section-cell--rest"
                            />
                          )}
                        </tr>
                      )
                    }
                    const cells = row.cells || {}
                    return (
                      <tr key={`${row.row_type}-${row.label}-${i}`}>
                        <td className="contract-report-preview__matrix-row-label">{row.label}</td>
                        <td className="contract-report-preview__num contract-report-preview__num--total-col">
                          {formatReportRub(row.row_total)}
                        </td>
                        {sortedColumns.map((c) => (
                          <td
                            key={c.stage_id}
                            className={[
                              'contract-report-preview__num',
                              `contract-report-preview__matrix-stage-cell--tone-${stageToneById[c.stage_id] || 'light'}`,
                            ].join(' ')}
                          >
                            {formatReportRub(cells[c.stage_id])}
                          </td>
                        ))}
                      </tr>
                    )
                  })
                )}
                {!loading && columns.length > 0 && matrixRowsWithSections.length > 0 && (
                  <>
                    <tr className="contract-report-preview__matrix-total-row">
                      <td className="contract-report-preview__matrix-row-label contract-report-preview__matrix-label-upper">
                        ИТОГО ДО НДС
                      </td>
                      <td className="contract-report-preview__num contract-report-preview__num--total-col">
                        {formatReportRub(
                          payload?.matrix_footer_grand_before ??
                            payload?.grand_total_before_vat ??
                            columns.reduce((s, c) => s + (Number(c.period_sum_before_vat) || 0), 0),
                        )}
                      </td>
                      {sortedColumns.map((c) => (
                        <td
                          key={c.stage_id}
                          className={[
                            'contract-report-preview__num',
                            `contract-report-preview__matrix-stage-cell--tone-${stageToneById[c.stage_id] || 'light'}`,
                          ].join(' ')}
                        >
                          {formatReportRub(
                            payload?.matrix_footer_before_by_stage?.[c.stage_id] ?? c.period_sum_before_vat,
                          )}
                        </td>
                      ))}
                    </tr>
                    <tr className="contract-report-preview__matrix-total-row">
                      <td className="contract-report-preview__matrix-row-label contract-report-preview__matrix-label-upper">
                        Итого после НДС
                      </td>
                      <td className="contract-report-preview__num contract-report-preview__num--total-col">
                        {formatReportRub(
                          payload?.matrix_footer_grand_after ?? payload?.grand_total,
                        )}
                      </td>
                      {sortedColumns.map((c) => (
                        <td
                          key={c.stage_id}
                          className={[
                            'contract-report-preview__num',
                            `contract-report-preview__matrix-stage-cell--tone-${stageToneById[c.stage_id] || 'light'}`,
                          ].join(' ')}
                        >
                          {formatReportRub(
                            payload?.matrix_footer_after_by_stage?.[c.stage_id] ?? columnTotals[c.stage_id],
                          )}
                        </td>
                      ))}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="contract-report-preview__section contract-report-preview__acts-section">
          <h3 className="contract-report-preview__section-title contract-report-preview__acts-title">
            АКТЫ ПО ДОГОВОРАМ
          </h3>
          <div className="contract-report-preview__table-wrap contract-report-preview__table-wrap--scroll">
            <table className="contract-report-preview__table">
              <thead>
                <tr>
                  <th>Акт</th>
                  <th>Статус и распределение</th>
                  <th>Объект</th>
                  <th>Заказчик</th>
                  <th>Договор</th>
                  <th>Стадия</th>
                  <th>Дата</th>
                  <th className="contract-report-preview__num">До НДС</th>
                  <th className="contract-report-preview__num">После налогов</th>
                  <th>Период</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10}>Загрузка…</td>
                  </tr>
                ) : acts.length === 0 ? (
                  <tr>
                    <td colSpan={10}>Нет актов за период</td>
                  </tr>
                ) : (
                  groupedActs.map((a, idx) => {
                    const mismatch = Boolean(a.distribution_mismatch)
                    const actAmountLabel = formatReportRub(a.amount)
                    const afterTaxLabel = formatReportRub(a.amount_after_tax)
                    const distSumLabel = formatReportRub(a.distribution_sum ?? 0)
                    let mismatchTitle
                    if (mismatch) {
                      if (a.amount_after_tax == null || a.amount_after_tax === 0) {
                        mismatchTitle = `Не заполнено «Сумма за вычетом налогов». Сумма акта: ${actAmountLabel}. Откройте акт для пересчёта.`
                      } else if ((a.distribution_count || 0) === 0) {
                        mismatchTitle = `Нет распределения в «Акты по сотрудникам». Сумма акта за вычетом налогов: ${afterTaxLabel}`
                      } else {
                        const afterNum = toNumber(a.amount_after_tax)
                        const distNum = toNumber(a.distribution_sum) ?? 0
                        const diff = afterNum == null ? null : Math.abs(afterNum - distNum)
                        const diffPct =
                          afterNum && afterNum > 0 && diff != null ? (diff / afterNum) * 100 : null
                        const diffLabel = diff != null ? formatReportRub(diff) : '—'
                        const pctLabel = diffPct != null ? `${diffPct.toFixed(1)}%` : '—'
                        mismatchTitle = `Сумма распределения (${distSumLabel}) не совпадает с суммой за вычетом налогов (${afterTaxLabel}). Разница: ${diffLabel} (${pctLabel})`
                      }
                    }
                    const rowClasses = [
                      mismatch ? 'contract-report-preview__row--mismatch' : '',
                      a.isFirstOfObject ? 'contract-report-preview__acts-object-start' : '',
                    ].filter(Boolean).join(' ') || undefined
                    return (
                      <tr
                        key={`${a.stage_id}-${a.issue_date}-${idx}`}
                        className={rowClasses}
                        title={mismatchTitle}
                      >
                        <td>
                          {a.act_id
                            ? linkCell(a.act_number || a.act_id, `/act/${encodeURIComponent(a.act_id)}`)
                            : <span>{a.act_number || '—'}</span>}
                        </td>
                        <td><ActBadges act={a} />{a.distribution_exclusion_reason && <div className="ds-detail-list__muted">{a.distribution_exclusion_reason}</div>}</td>
                        {a.isFirstOfObject && (
                          <td rowSpan={a.objectRowSpan} className="contract-report-preview__acts-group-cell">
                            {a.object_id
                              ? linkCell(a.object_name || '—', `/object/${encodeURIComponent(a.object_id)}`, { newTab: true })
                              : (a.object_name || '—')}
                          </td>
                        )}
                        {a.isFirstOfContract && (
                          <td rowSpan={a.contractRowSpan} className="contract-report-preview__acts-group-cell">
                            {a.client_id
                              ? linkCell(a.client_name || '—', `/client/${encodeURIComponent(a.client_id)}`)
                              : (a.client_name || '—')}
                          </td>
                        )}
                        {a.isFirstOfContract && (
                          <td rowSpan={a.contractRowSpan} className="contract-report-preview__acts-group-cell">
                            {a.contract_id
                              ? linkCell(a.contract_number || '—', `/contract/${encodeURIComponent(a.contract_id)}`)
                              : (a.contract_number || '—')}
                          </td>
                        )}
                        <td>
                          {a.stage_id
                            ? linkCell(a.stage_display || a.stage_id, `/stage/${encodeURIComponent(a.stage_id)}`, { newTab: true })
                            : (a.stage_display || '—')}
                        </td>
                        <td>{formatActDate(a.issue_date)}</td>
                        <td className="contract-report-preview__num">{actAmountLabel}</td>
                        <td className="contract-report-preview__num">{formatReportRub(a.amount_after_tax)}</td>
                        <td>{a.period || periodLabel}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      {reportId && <EntityComments entityType="reports" entityId={reportId} />}
    </div>
  )
}

export default SummaryActsReportPreview
